// @spec developer-tools-mock-mode authentication-reauth threads
// Layout constants
export const MAX_CONTENT_WIDTH = 'max-w-4xl';

// Dev-only constants
export const MOCK_MODE_COOKIE = 'fpo3-mock-mode';
export const MOCK_SCENARIO_COOKIE = 'fpo3-mock-scenario';
export const MOCK_HYDRATION_COOKIE = 'fpo3-mock-hydration';
export const MOCK_MODE_SIMULATED_VALUE = 'simulated';
/**
 * Dev-only: per-test isolation key set by Playwright's `setupScenario()` in
 * simulated mode. Derives the UUID-based familyId/userId for per-test DB
 * isolation and selects the per-test GCal mock and observable call arrays.
 */
export const MOCK_TEST_ID_COOKIE = 'fpo3-test-id';
/**
 * Dev-only: drives the Google reauth banner under mock mode so it can be
 * visually tested without a real broken Google account. Set by the mock-mode
 * banner controls; read by `GoogleReauthBanner` when mock mode is active.
 * Absent/empty = banner hidden; `'reauth'` = token-failure variant;
 * `'email'` = email-scope-missing variant (FPO-1712).
 */
export const MOCK_REAUTH_COOKIE = 'fpo3-mock-reauth';
export type MockReauthBannerState = 'reauth' | 'email';

// Google reauth
export const GOOGLE_REAUTH_EVENT = 'google-reauth-required';

/** Cookie set before reauth redirect so the server can reject wrong-account sign-ins. */
export const REAUTH_EXPECTED_COOKIE = 'fpo3-reauth-expected';

// Link-flow cookies, max-ages, and the LinkStatusValue type live in
// `@fpo3/famfam-core/account-linking`. Importers should pull them directly
// from there rather than going through this grab-bag.

/** Cookie that gates the sign-in page behind an access code. */
export const ACCESS_GATE_COOKIE = 'ff-gate';

/** Cookie set during cross-env login pre-OAuth leg to prove the post-OAuth
 *  return came through the legitimate flow. */
export const CROSS_ENV_STATE_COOKIE = 'cross-env-state';

/** Base name for the non-secure NextAuth session cookie. */
export const SESSION_COOKIE_BASE = 'authjs.session-token';

/** Base name for the secure (HTTPS) NextAuth session cookie. */
export const SESSION_COOKIE_SECURE_BASE: string = `__Secure-${SESSION_COOKIE_BASE}`;

/** Both session cookie base names. Used by client-side clearing, server-side
 *  detection, and cross-env token encoding. Large JWTs produce chunked variants
 *  like `authjs.session-token.0` through `.4`. */
export const SESSION_COOKIE_BASES: readonly string[] = [
  SESSION_COOKIE_BASE,
  SESSION_COOKIE_SECURE_BASE,
];

/** Payload for the GOOGLE_REAUTH_EVENT custom event. */
export interface GoogleReauthEventDetail {
  reason?: string;
}

// Cross-component events
/** Dispatched on `window` when conversations are created or modified outside the normal
 *  thread-analysis flow (e.g. chat `create_thread` tool). Hooks like
 *  `useThreads` listen for this to trigger a refetch. */
export const CONVERSATIONS_CHANGED_EVENT = 'famfam:threads-changed';

/** Dispatched on `window` when calendar events are created, deleted, or modified
 *  outside the normal calendar flow (e.g. chat calendar tools). Hooks like
 *  `useCalendarEvents` listen for this to trigger a refetch. */
export const CALENDAR_CHANGED_EVENT = 'famfam:calendar-changed';

/** Dispatched on `window` when a reconciled (email-implied) row's `userAction`
 *  changes (Accept / Mark irrelevant). Hook subscribers refetch the visible
 *  range so the ghost overlay reflects the new state. Distinct from
 *  `CALENDAR_CHANGED_EVENT` — a reconciled action does not (yet) imply a gcal
 *  mutation. */
export const RECONCILED_CHANGED_EVENT = 'famfam:reconciled-changed';

/** Dispatched by `ReconciledEventPopover` after a successful Accept. The
 *  payload carries the source ghost event so a top-level listener
 *  (calendar-view) can seed the create-event editor with a real-shape draft. */
export const RECONCILED_ACCEPT_EVENT = 'famfam:reconciled-accept';

// Agent identity
/** Author ID stored in Thread.createdBy and ThreadComment.authorId for AI-generated content */
export const AGENT_AUTHOR_ID = 'famfam';

/** Display name for the AI agent in thread UIs */
export const AGENT_DISPLAY_NAME = 'Famfam';

/** Fallback display name for a whole-family calendar when its real name is not
 *  yet available (e.g. provider not loaded, legacy event without a resolved
 *  calendar entry). Used by WholeFamilyPill and useMoveDestinations so the
 *  user-facing label is defined in one place. */
export const WHOLE_FAMILY_FALLBACK_DISPLAY_NAME = 'Whole Family';
